import { AlertCircleIcon, CalendarCheck2Icon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	BypassCheckRow,
	BypassChecksPanel,
} from "@/components/admin-portal/appointment/bypass-checks-panel";
import { MitraAssignmentReasonSelect } from "@/components/admin-portal/appointment/mitra-assignment-reason";
import { Button } from "@/components/ui/button";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { ReasonOption } from "@/lib/appointments/reason-label";

interface ConfirmationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	isLoading: boolean;
	summary: Array<{
		visitNumber: number;
		oldDateTime?: Date | null;
		newDateTime?: Date | null;
		oldTherapistId?: string | null;
		newTherapistId?: string | null;
	}>;
	reason: string;
	onReasonChange: (value: string) => void;
	sendEmailNotification: boolean;
	onSendEmailNotificationChange: (value: boolean) => void;
	bypassSlotConflict: boolean;
	onBypassSlotConflictChange: (value: boolean) => void;
	bypassContractCheck: boolean;
	onBypassContractCheckChange: (value: boolean) => void;
	bypassJourneyCheck: boolean;
	onBypassJourneyCheckChange: (value: boolean) => void;
	// Present only while a visit newly goes to a Mitra and the order has no reason yet.
	mitraReason?: {
		options: ReasonOption[];
		value: string;
		onChange: (value: string) => void;
		required: boolean;
	};
}

export function ConfirmationModal({
	isOpen,
	onClose,
	onConfirm,
	isLoading,
	summary,
	reason,
	onReasonChange,
	sendEmailNotification,
	onSendEmailNotificationChange,
	bypassSlotConflict,
	onBypassSlotConflictChange,
	bypassContractCheck,
	onBypassContractCheckChange,
	bypassJourneyCheck,
	onBypassJourneyCheckChange,
	mitraReason,
}: ConfirmationModalProps) {
	const { t: tasf } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.fields.mitra_assignment_reason",
	});
	const missingMitraReason = !!mitraReason?.required && !mitraReason.value;
	const { t } = useTranslation("appointments-form");
	const modifiedCount = useMemo(() => {
		return summary.filter(
			(item) =>
				item.oldDateTime?.toISOString() !== item.newDateTime?.toISOString() ||
				item.oldTherapistId !== item.newTherapistId,
		).length;
	}, [summary]);

	return (
		<Drawer open={isOpen} onOpenChange={onClose}>
			<DrawerContent className="max-h-screen">
				{/* Wider at md+ so the attention panel and the inputs can sit side by side. */}
				<div className="mx-auto w-full md:max-w-3xl">
					<DrawerHeader>
						<div className="mx-auto mb-2 border border-primary/25 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
							<CalendarCheck2Icon className="h-6 w-6 text-primary" />
						</div>
						<DrawerTitle className="text-center">
							{t("bulk_reschedule.dialogs.save.title", "Confirm Bulk Changes")}
						</DrawerTitle>
						<DrawerDescription className="text-center text-balance">
							{t("bulk_reschedule.dialogs.save.description", {
								count: modifiedCount,
								defaultValue: `You are about to commit ${modifiedCount} visit modifications to this booking series.`,
							})}
						</DrawerDescription>
					</DrawerHeader>

					{/* md+: attention on the left, the inputs on the right. */}
					{/* pb clears the sticky footer, which overlays this area: it is pinned to the
					    viewport, not laid out below the scroll box. */}
					<div className="no-scrollbar overflow-y-auto p-4 pb-32 md:pb-40 lg:pb-24 max-h-[75dvh] md:max-h-[88dvh] grid gap-6 md:grid-cols-2 md:items-start">
						<div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900/50 dark:bg-amber-950/30">
							<div className="flex gap-3 text-sm">
								<AlertCircleIcon className="size-5 shrink-0 text-amber-600 dark:text-amber-500" />
								<div className="space-y-2">
									<p className="font-bold text-amber-900 dark:text-amber-200 uppercase tracking-tight">
										{t(
											"bulk_reschedule.dialogs.save.attention_title",
											"Attention: Review Final Sequence",
										)}
									</p>
									<div className="text-sm leading-normal text-amber-800/90 dark:text-amber-300/80 space-y-2">
										<p>
											{t(
												"bulk_reschedule.dialogs.save.attention_desc",
												"By confirming, the chronological sequence you see in the previous list will become the new permanent order for this booking.",
											)}
										</p>
										<ul className="list-disc pl-4 space-y-1 leading-normal">
											<li>
												{t(
													"bulk_reschedule.dialogs.save.note_scheduled",
													"Visits with a selected date & time will be Scheduled according to their new order.",
												)}
											</li>
											<li>
												{t(
													"bulk_reschedule.dialogs.save.note_unscheduled",
													"Visits without a date will be moved to Unscheduled status.",
												)}
											</li>
											<li>
												{t(
													"bulk_reschedule.dialogs.save.note_therapists",
													"Assigned therapists will be updated or removed immediately.",
												)}
											</li>
										</ul>
									</div>
								</div>
							</div>
						</div>

						<div className="space-y-6">
							<div className="space-y-2">
								<label
									htmlFor="bulk-reschedule-reason"
									className="text-sm font-medium"
								>
									{t(
										"bulk_reschedule.dialogs.save.reason_label",
										"Reason for Reschedule",
									)}
								</label>
								<Textarea
									id="bulk-reschedule-reason"
									placeholder={t(
										"bulk_reschedule.dialogs.save.reason_placeholder",
										"Enter the reason for this bulk reschedule...",
									)}
									value={reason}
									onChange={(e) => onReasonChange(e.target.value)}
									className="bg-input shadow-inner"
									rows={3}
								/>
							</div>

							{mitraReason && (
								<div className="space-y-2">
									<p className="text-sm font-medium">{tasf("label")}</p>
									<MitraAssignmentReasonSelect
										options={mitraReason.options}
										value={mitraReason.value}
										onChange={mitraReason.onChange}
										invalid={missingMitraReason}
									/>
									<p className="text-xs text-muted-foreground text-pretty">
										{missingMitraReason
											? tasf("errors.required")
											: tasf("description")}
									</p>
								</div>
							)}

							<div className="relative flex w-full items-start gap-2 rounded-md border border-input p-4 shadow-xs outline-none bg-input has-data-[state=checked]:border-primary/50">
								<Switch
									id="bulk-reschedule-send-email"
									checked={sendEmailNotification}
									onCheckedChange={onSendEmailNotificationChange}
									aria-describedby="bulk-reschedule-send-email-description"
									className="data-[state=checked]:[&_span]:rtl:-translate-x-2 order-1 h-4 w-6 after:absolute after:inset-0 [&_span]:size-3 data-[state=checked]:[&_span]:translate-x-2"
								/>
								<div className="grid grow gap-2">
									<Label htmlFor="bulk-reschedule-send-email">
										{t(
											"bulk_reschedule.dialogs.save.send_email_label",
											"Send Email Notification",
										)}{" "}
										<span className="font-normal text-muted-foreground text-xs leading-[inherit]">
											(Optional)
										</span>
									</Label>
									<p
										className="text-muted-foreground text-xs"
										id="bulk-reschedule-send-email-description"
									>
										{t(
											"bulk_reschedule.dialogs.save.send_email_desc",
											"Notify the patient about the rescheduled appointments via email.",
										)}
									</p>
								</div>
							</div>
						</div>

						{/* Full-width row spanning both columns so the overrides sit
						    2-up on large screens instead of cramped in the right column. */}
						<BypassChecksPanel cols={2}>
							<BypassCheckRow
								id="bulk-bypass-slot-conflict"
								keyPrefix="bypass_slot_conflict"
								checked={bypassSlotConflict}
								onCheckedChange={onBypassSlotConflictChange}
							/>
							<BypassCheckRow
								id="bulk-bypass-contract-check"
								keyPrefix="bypass_contract_check"
								checked={bypassContractCheck}
								onCheckedChange={onBypassContractCheckChange}
							/>
							<BypassCheckRow
								id="bulk-bypass-journey-check"
								keyPrefix="bypass_journey_check"
								checked={bypassJourneyCheck}
								onCheckedChange={onBypassJourneyCheckChange}
							/>
						</BypassChecksPanel>
					</div>

					<DrawerFooter className="sticky bottom-0 z-10 border-t bg-background gap-4 md:gap-2 flex-col md:flex-row">
						<Button
							className="w-full md:flex-1"
							disabled={isLoading || missingMitraReason}
							onClick={onConfirm}
						>
							{isLoading
								? t("button.save.loading", "Saving...")
								: t(
										"bulk_reschedule.dialogs.save.confirm",
										"Confirm & Save Changes",
									)}
						</Button>

						<Button
							variant="ghost"
							className="w-full md:w-auto"
							onClick={onClose}
							disabled={isLoading}
						>
							{t("bulk_reschedule.dialogs.save.cancel", "Go Back & Review")}
						</Button>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	);
}
